import type { ParsedUrlQuery } from 'querystring';

const UPPERCASE = /[A-Z]/;

const NORDIC_CHARACTERS = /[æøåäÆØÅÄ]/;

/**
 * Appends every param of a parsed query (repeated keys included) to `params`, except the keys `shouldSkip` returns true for.
 */
export const appendQueryParams = (
    params: URLSearchParams,
    query: ParsedUrlQuery,
    shouldSkip: (key: string) => boolean,
) => {
    Object.entries(query).forEach(([key, value]) => {
        if (shouldSkip(key)) return;

        [value].flat().forEach((item) => {
            if (item !== undefined) params.append(key, item);
        });
    });
};

/**
 * Creates a URL object from a relative path, ensuring it works across all environments
 */
export const createAbsoluteUrl = (relativeUrl: string) => {
    try {
        // Handle cases where relativeUrl might be undefined or null and Remove any leading double slashes that might cause URL parsing issues
        const safeRelativeUrl = relativeUrl || '/';
        const cleanRelativeUrl = safeRelativeUrl.replace(/^\/\/+/, '/');

        if (typeof window !== 'undefined') {
            return new URL(cleanRelativeUrl, window.location.origin);
        }

        // For SSR, use NEXT_PUBLIC_URL if available, otherwise use a default
        const baseUrl = process.env.NEXT_PUBLIC_URL || 'https://imerco.dk';
        return new URL(cleanRelativeUrl, baseUrl);
    } catch (error) {
        // If URL construction fails, create a safe fallback URL
        const baseUrl =
            typeof window !== 'undefined'
                ? window.location.origin
                : process.env.NEXT_PUBLIC_URL || 'https://imerco.dk';
        const fallbackUrl = new URL(baseUrl);
        // Ensure the pathname is safe
        fallbackUrl.pathname = (relativeUrl || '/').replace(/^\/\/+/, '/');
        return fallbackUrl;
    }
};

export const containsUpperOrNordicChars = (relativeUrl: string) => {
    try {
        const url = createAbsoluteUrl(relativeUrl);
        const decodedPath = decodeURIComponent(url.pathname);
        return UPPERCASE.test(decodedPath) || NORDIC_CHARACTERS.test(decodedPath);
    } catch {
        // In case of any errors, return false
        return false;
    }
};

/**
 * Normalizes a URL to ensure its pathname has:
 *   1) Special characters replaced with their ASCII fallback (æ→ae, ø→oe, å→aa, ä→ae, é→e, etc.)
 *   2) Spaces converted to dashes
 *   3) Is converted to lowercase
 *   4) Leaves query intact
 *   5) Works consistently across all environments
 */
export const normalizePath = (relativeUrl: string) => {
    try {
        // Handle empty or invalid input
        if (!relativeUrl) return '/';

        const url = createAbsoluteUrl(relativeUrl);
        const decodedPath = decodeURIComponent(url.pathname);

        let replaced = decodedPath
            // Handle Nordic and German characters first
            .replace(/[æÆäÄ]/g, 'ae')
            .replace(/[øØöÖ]/g, 'oe')
            .replace(/[üÜ]/g, 'ue')
            .replace(/[åÅ]/g, 'aa')
            // Handle other accented characters
            .replace(/[éèêë]/g, 'e')
            .replace(/[áàâã]/g, 'a')
            .replace(/[íìîï]/g, 'i')
            .replace(/[óòôõ]/g, 'o')
            .replace(/[ýÿ]/g, 'y')
            .replace(/[ñ]/g, 'n')
            // Handle special characters
            .replace(/'/g, '') // remove apostrophes
            .replace(/\s&\s/g, '-') // & with spaces becomes dash
            .replace(/([^\s])&([^\s])/g, '$1$2') // & without spaces is removed
            .replace(/[\s_]+/g, '-') // spaces and underscores to dash
            .replace(/[,;]+/g, '-') // commas and semicolons to dash
            .replace(/[()[\]{}]+/g, '') // remove brackets and parentheses
            .replace(/-+/g, '-') // clean up multiple hyphens
            .replace(/^-+|-+$/g, '') // remove leading/trailing hyphens
            .toLowerCase();

        // Clean up any malformed path segments
        replaced = replaced
            .replace(/\/+/g, '/') // Remove multiple consecutive slashes
            .replace(/^\/\/+/, '/'); // Remove leading double slashes

        replaced = encodeURI(replaced);

        // Preserve the original query string if it exists
        const originalUrl = new URL(
            relativeUrl,
            typeof window !== 'undefined'
                ? window.location.origin
                : process.env.NEXT_PUBLIC_URL || 'https://imerco.dk',
        );
        const queryString = originalUrl.search || '';

        // Return just the path + query for relative URLs
        return replaced + queryString;
    } catch (error) {
        // If normalization fails, return a safe fallback
        return relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;
    }
};
